// Epic 33 — "edit a week from the Summary". Clicking a week row opens this modal,
// which fetches that (user, year, week)'s week plan, mounts the SAME WeekPlanCard
// editor used on the Week Plan page (restore hidden), and saves via the existing
// endpoint. On success it refreshes the Summary so the actual-time totals update.
import React, { useEffect, useState } from "react";
import { Modal, Button, Spin, Empty, Alert, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useKeycloak } from "@react-keycloak/web";
import { useTranslation } from "react-i18next";
import { WeekPlanCard } from "../weekPlan/WeekPlanCard";
import { WeekPlanService } from "../../services/weekPlanService";
import { getCurrentSubOrganisations } from "../../redux/slieces/subOrganisationSlice";
import { getSummaryEmployee } from "../../redux/slieces/summaryEmplyeeSlice";
import {
  setWorkDurationTime,
  setDayStatus,
  setComment,
  setSubOrganisation,
  addWorkDuration,
  removeWorkDuration,
  hasShiftErrors,
} from "../../core/weekplan/weekPlanEditing";

const WeekDetailModal = ({ open, onClose, userId, year, week, userName }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { keycloak } = useKeycloak();
  const hasMediAdminRole = keycloak?.hasRealmRole?.("mediadmin") || false;
  // The store keeps the list at subOrganisationData.subOrganisations.data
  // (mirrors WeekPlanContent). Reading the object instead of the array made
  // WeekPlanCard's `subOrganisations.map` crash.
  const subOrganisations = useSelector((s) => s.subOrganisationData?.subOrganisations?.data) || [];

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalPlan, setModalPlan] = useState(null);

  // Fetch the tapped week directly (NOT via the shared weekPlan thunk, which would
  // clobber the Week Plan page's store slice) and isolate the clicked user's row.
  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    setModalPlan(null);
    setLoading(true);
    if (!subOrganisations || subOrganisations.length === 0) {
      dispatch(getCurrentSubOrganisations());
    }
    WeekPlanService.getUserWeekPlan(week, year, false)
      .then((list) => {
        if (cancelled) return;
        const arr = Array.isArray(list) ? list : list?.weekPlanSchedules || [];
        setModalPlan(arr.find((w) => w.userId === userId) || null);
      })
      .catch((err) => {
        if (!cancelled) message.error(err?.message || t("errorSavingWeekPlan") || "Error loading week");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, userId, week, year, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Editing handlers operate on the single fetched plan via the shared, pure
  // transforms (wrap in an array of one; the transforms filter by userId).
  const first = (arr) => (arr && arr[0]) || null;
  const handleTimeChange = (uid, day, idx, type, ts) =>
    setModalPlan((p) => (p ? first(setWorkDurationTime([p], uid, day, idx, type, ts, year, week)) : p));
  const updateDayStatus = (uid, day, status) =>
    setModalPlan((p) => (p ? first(setDayStatus([p], uid, day, status)) : p));
  const handleCommentChange = (uid, day, comment) =>
    setModalPlan((p) => (p ? first(setComment([p], uid, day, comment)) : p));
  const handleSubOrganisationChange = (uid, day, soId) =>
    setModalPlan((p) => (p ? first(setSubOrganisation([p], uid, day, soId, subOrganisations)) : p));
  const handleAddWorkDuration = (uid, day) =>
    setModalPlan((p) => (p ? first(addWorkDuration([p], uid, day, year, week)) : p));
  const handleRemoveWorkDuration = (uid, day, idx) => {
    const dp = modalPlan?.calendarEmployees?.[0]?.dailyPlanEmployees?.find((d) => d.day === day);
    if ((dp?.workDurationEmployees?.length || 0) <= 1) {
      message.warning(t("cannotDeleteLastDuration") || "Cannot delete the last work duration");
      return;
    }
    setModalPlan((p) => (p ? first(removeWorkDuration([p], uid, day, idx)) : p));
  };
  const handleActivateChanges = (uid, w, y, checked) =>
    setModalPlan((p) =>
      p ? { ...p, calendarEmployees: p.calendarEmployees.map((c) => ({ ...c, activate: checked })) } : p
    );
  const noop = () => {};

  const invalidShifts = hasShiftErrors(modalPlan);

  const handleSave = async () => {
    if (!modalPlan) return;
    if (invalidShifts) {
      message.error(t("invalidShiftTimes") || "Please fix the shift times before saving.");
      return;
    }
    setSaving(true);
    try {
      await WeekPlanService.saveUserWeekPlan([modalPlan], week, year);
      message.success(t("weekPlanSavedSuccessfully") || "Saved");
      dispatch(getSummaryEmployee()); // refresh summary totals
      onClose();
    } catch (err) {
      message.error(err?.message || t("errorSavingWeekPlan") || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      centered
      width={680}
      styles={{ body: { maxHeight: "72vh", overflowY: "auto" } }}
      maskClosable={false}
      title={t("editWeekTitle", { week, year }) || `Week ${week} / ${year}`}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("cancel") || "Cancel"}
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          disabled={!modalPlan || invalidShifts}
          onClick={handleSave}
        >
          {t("saveChanges") || "Save"}
        </Button>,
      ]}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin />
        </div>
      ) : !modalPlan ? (
        <Empty description={t("noDataForYears") || "No data for this week"} />
      ) : (
        <>
        {invalidShifts && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={t("invalidShiftTimes") || "Please fix the shift times before saving."}
          />
        )}
        <WeekPlanCard
          currentWeekPlan={modalPlan}
          currentCulture="de-DE"
          updateCurrentWeekPlan={noop}
          deleteUserWeekPlan={noop}
          hideRestore
          hideActivate
          selectedWeek={week}
          selectedYear={year}
          handleTimeChange={handleTimeChange}
          updateDayStatus={updateDayStatus}
          isMobile={true}
          handleActivateChanges={handleActivateChanges}
          handleCommentChange={handleCommentChange}
          handleSubOrganisationChange={handleSubOrganisationChange}
          subOrganisations={subOrganisations}
          hasMediAdminRole={hasMediAdminRole}
          weekPlanSettings={{}}
          users={[]}
          handleAddWorkDuration={handleAddWorkDuration}
          handleRemoveWorkDuration={handleRemoveWorkDuration}
        />
        </>
      )}
    </Modal>
  );
};

export default WeekDetailModal;
